import { z } from 'zod';
/**
 * What a reviewer is asked, split by the thing being judged.
 *
 * The wording comes from the "Pytania do szachistów" page of the v4 report, so
 * a reviewer who read the PDF meets the same questions here. On paper they are
 * one list of fifteen; a list of fifteen answered once per report tells us
 * nothing about which board went wrong, so they are split three ways: what can
 * only be judged on a single board, what is a property of the axis, and what
 * is about the set as a whole.
 */
export const answerChoice = z.enum(['trafny', 'czesciowo', 'nietrafny']);
/**
 * Retired from the board form (owner, 2026-08-03), kept so the answers already
 * given still round-trip through the API and the export.
 */
export const forcedChoice = z.enum(['preferencja', 'wymuszony', 'nie_wiem']);
/**
 * Three degrees, not a yes/no with an escape hatch (owner, 2026-08-03).
 *
 * The first three values are the scale in use; `tak`/`nie`/`nie_wiem` are the
 * first wording of this question and stay accepted so answers already stored
 * still validate on their way back out.
 */
export const naturalChoice = z.enum([
    'naturalny',
    'czesciowo',
    'sztuczny',
    'tak',
    'nie',
    'nie_wiem',
]);
/** Placement of one example on the style map.
 *
 * The first three values already occur in stored answers. `nie_pasuje` adds
 * the useful distinction between "this belongs on another axis" and "this
 * example should not be evidence for any axis" without requiring a database
 * migration: SQLite stores the value as text.
 */
export const poleChoice = z.enum(['tak', 'nie', 'inna_os', 'nie_pasuje']);
export const fitChoice = z.enum(['tak', 'czesciowo', 'nie']);
export const BOARD_QUESTIONS = [
    {
        id: 'accuracy',
        source: 8,
        prompt: 'Czy komentarz wiernie oddaje to, co widać na szachownicy?',
        options: [
            { value: 'trafny', label: 'trafny', tone: 'green' },
            { value: 'czesciowo', label: 'częściowo', tone: 'amber' },
            { value: 'nietrafny', label: 'nietrafny', tone: 'red' },
        ],
    },
    {
        id: 'altNatural',
        source: 5,
        prompt: 'Czy alternatywa to naturalny ruch?',
        options: [
            { value: 'naturalny', label: 'naturalny', tone: 'green' },
            { value: 'czesciowo', label: 'częściowo', tone: 'amber' },
            { value: 'sztuczny', label: 'sztuczny', tone: 'red' },
        ],
    },
    {
        id: 'polePlacement',
        source: 2,
        prompt: 'Czy przykład stoi pod właściwym biegunem pokazanej osi?',
        options: [
            { value: 'tak', label: 'tak, właściwy biegun', tone: 'green' },
            { value: 'nie', label: 'nie, drugi biegun', tone: 'amber' },
            { value: 'inna_os', label: 'to inna oś', tone: 'red' },
            { value: 'nie_pasuje', label: 'nie pasuje do osi', tone: 'red' },
        ],
    },
];
export const AXIS_QUESTIONS = [
    {
        id: 'poleNameFits',
        source: 12,
        prompt: 'Czy nazwa bieguna opisuje wszystkie pozycje, które pod nim zebraliśmy?',
        options: [
            { value: 'tak', label: 'tak', tone: 'green' },
            { value: 'czesciowo', label: 'częściowo', tone: 'amber' },
            { value: 'nie', label: 'nie', tone: 'red' },
        ],
    },
    {
        id: 'poleNamePrecise',
        source: 13,
        prompt: 'Czy nazwy biegunów są na tyle precyzyjne, by dwóch ekspertów przypisało tę samą pozycję tak samo?',
        options: [
            { value: 'tak', label: 'tak', tone: 'green' },
            { value: 'czesciowo', label: 'częściowo', tone: 'amber' },
            { value: 'nie', label: 'nie', tone: 'red' },
        ],
    },
    {
        id: 'phaseCaveat',
        source: 15,
        prompt: 'Czy oś zachowuje sens w debiucie, grze środkowej i końcówce tak samo?',
        options: [
            { value: 'tak', label: 'tak', tone: 'green' },
            { value: 'czesciowo', label: 'zależy od fazy', tone: 'amber' },
            { value: 'nie', label: 'nie', tone: 'red' },
        ],
    },
];
export const FINAL_QUESTIONS = [
    {
        id: 'exercises',
        source: 1,
        prompt: 'Czy na podstawie tych dowodów da się zbudować zadania szachowe różnicujące styl zawodnika?',
        options: [
            { value: 'tak', label: 'tak', tone: 'green' },
            { value: 'czesciowo', label: 'częściowo', tone: 'amber' },
            { value: 'nie', label: 'nie', tone: 'red' },
        ],
    },
];
export const FINAL_TEXT_QUESTIONS = [
    {
        id: 'exclusions',
        source: 14,
        prompt: 'Jakie sytuacje powinny automatycznie wykluczać przykład z danej osi?',
    },
    {
        id: 'general',
        source: 0,
        prompt: 'Cokolwiek jeszcze, co warto nam powiedzieć o tym zestawie.',
    },
];
/**
 * Asked once per wording when a board carries two.
 *
 * Same scale as the single-wording verdict, so the two rounds are comparable:
 * a board that scored "trafny" before an A/B round and "trafny" for variant A
 * during one means the same thing.
 */
export const VARIANT_VERDICT_QUESTION = {
    prompt: 'Ten opis jest',
    options: [
        { value: 'trafny', label: 'trafny', tone: 'green' },
        { value: 'czesciowo', label: 'częściowo', tone: 'amber' },
        { value: 'nietrafny', label: 'nietrafny', tone: 'red' },
    ],
};
/** Which wording won, asked after both have been judged. */
export const BETTER_VARIANT_OPTIONS = [
    { value: 'a', label: 'wersja A', tone: 'green' },
    { value: 'b', label: 'wersja B', tone: 'green' },
    { value: 'both', label: 'oba dobre', tone: 'neutral' },
    { value: 'neither', label: 'oba słabe', tone: 'red' },
];
//# sourceMappingURL=questions.js.map